import { HrefLink as Link } from "@/components/ui/href-link";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, ArrowLeft } from "lucide-react";

import {
  OverviewBanners,
  SectionHead,
  TrendFigures,
  activeMaintenance,
  chartInstant,
} from "@/components/dashboard/overview-panels";
import { PersonAvatar } from "@/components/employees/person-avatar";
import { PageHeader } from "@/components/dashboard/page-header";
import { StatusPill } from "@/components/dashboard/status-pill";
import { Sparkline } from "@/components/dashboard/charts/sparkline";
import { incidentStatusCounts, listIncidentReports } from "@/api/incidents";
import { listMaintenance } from "@/api/maintenance";
import {
  attendance as attendanceCopy,
  common,
  nav,
  overview as copy,
  roles,
  tracking as trackingCopy,
} from "@/content";
import { useLocale } from "@/context/locale-provider";
import { can } from "@/constants/permissions";
import { getOverviewPayload } from "@/api/overview";
import { toDateKey } from "@/utils/time";
import { cn } from "@/utils";
import { useFmt } from "@/hooks/shared/useFmt";
import { useSession } from "@/context/UserContext";
import { PageError, PageLoading } from "@/components/dashboard/page-state";
import { useQuery } from "@tanstack/react-query";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";

/**
 * The Overview.
 *
 * Built last, because of one rule that shapes the whole screen: **every tile
 * links to a filtered list that exists.** A number an operator cannot act on is
 * decoration, and a dashboard made of decoration teaches people to stop looking
 * at it. Where a count is zero there is nothing to link to, so the tile says so
 * quietly and does not pretend to be a destination.
 *
 * The prototype had no overview at all — `/` was a role redirect, and a guard
 * who landed there got a bare "no dashboard access" text node whose own code
 * comment explains it exists to break an infinite redirect loop.
 *
 * A supervisor sees their own team and the sites their team staffs. An admin sees
 * everything.
 */
export const Route = createFileRoute("/_main/dashboard/")({
  component: OverviewPage,
});

function OverviewPage() {
  const { user, settings } = useSession();
  const { locale } = useLocale();
  useDocumentTitle(nav.overview);

  const fmt = useFmt();
  const canIncidents = can(user.role, "incidents.read");
  const canMaintenance = can(user.role, "maintenance.read");

  /* One call for all four aggregates. The API assembles them together — the scope
     from the session, the staleness threshold from the settings row — because four
     round trips would show the operator this screen building itself panel by
     panel, on the one page whose job is to be readable at a glance.

     Thirty seconds, and refetched on focus: this is the screen left open on a wall
     display, and "on duty now" is worthless if it is an hour old. */
  const overview = useQuery({
    queryKey: ["overview", "summary"],
    queryFn: getOverviewPayload,
    refetchInterval: 30_000,
    staleTime: 0,
  });

  const openIncidents = useQuery({
    queryKey: ["overview", "incidents"],
    enabled: canIncidents,
    refetchInterval: 30_000,
    queryFn: async () => {
      const [counts, page] = await Promise.all([
        incidentStatusCounts(),
        listIncidentReports({
          search: "",
          sort: { column: "createdAt", direction: "desc" },
          page: 1,
          pageSize: 8,
        }),
      ]);
      const latest =
        page.rows.find((row) => row.status === "new" || row.status === "under_review") ?? null;
      return { open: counts.new + counts.under_review, latest };
    },
  });

  const maintenanceWindows = useQuery({
    queryKey: ["overview", "maintenance"],
    enabled: canMaintenance,
    queryFn: listMaintenance,
    refetchInterval: 60_000,
  });

  /* Returned early rather than wrapped, so everything below reads the way it did
     when the server had already awaited it. This is the one screen where a
     partial render would be actively misleading: a coverage tile with no number
     beside a populated alert list looks like a real reading of zero.

     A background refetch failure must not wipe a still-valid payload — that is
     how a 30s poll blip looked like the whole Overview was down. */
  if (overview.isPending) return <PageLoading shape="tiles" />;
  if (overview.isError && !overview.data) {
    return (
      <PageError
        error={overview.error}
        onRetry={() => {
          void overview.refetch();
        }}
      />
    );
  }
  if (!overview.data) return <PageLoading shape="tiles" />;

  const { data, gaps, activity, sparkline } = overview.data;

  const today = toDateKey(new Date(), settings.timeZone);

  /* The alert list is built from the same counts as the tiles, and only the
     non-zero ones appear. A permanent list of green ticks is noise; a list that
     is empty when nothing is wrong is a signal. */
  const alerts = [
    {
      label: copy.alertLeave,
      count: data.pendingLeave,
      href: "/dashboard/leave?status=pending",
      visible: can(user.role, "leave.read"),
    },
    {
      label: copy.alertUnclosed,
      count: data.unclosedPunches,
      href: "/dashboard/attendance",
      visible: can(user.role, "attendance.read"),
    },
    {
      label: copy.alertGeofence,
      count: data.geofenceFlagsToday,
      href: `/dashboard/attendance?status=outside_zone&from=${today}&to=${today}`,
      visible: can(user.role, "attendance.read"),
    },
    {
      label: copy.alertStale,
      count: data.staleSignals,
      href: "/dashboard/tracking",
      visible: can(user.role, "tracking.read"),
    },
    {
      label: copy.alertUnderstaffed,
      count: data.understaffedZones,
      href: "/dashboard/zones",
      visible: can(user.role, "zones.read"),
    },
    {
      label: copy.alertInvites,
      count: data.pendingInvites,
      href: "/dashboard/employees?status=active",
      visible: can(user.role, "employees.read"),
    },
    {
      label: copy.alertCompanyContracts,
      count: data.expiringCompanyContracts ?? 0,
      href: "/dashboard/companies",
      visible: can(user.role, "companies.read"),
    },
  ].filter((alert) => alert.visible && alert.count > 0);

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader
        eyebrowMark
        eyebrow={nav.groups.monitoring}
        title={`${copy.greeting} ${user.name} — ${roles[user.role]}`}
        lead={`${roles[user.role]} · ${copy.lead}`}
      />

      <OverviewBanners
        incident={canIncidents ? openIncidents.data : undefined}
        maintenance={
          canMaintenance
            ? activeMaintenance(maintenanceWindows.data ?? [], locale)
            : undefined
        }
      />

      {/* `gap-px` on `bg-n4`: the lines between tiles are the gaps. Not a row of
          cards — the design system's sectioning device throughout. */}
      <dl className="mt-8 grid gap-px overflow-hidden rounded-lg bg-n4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          label={copy.onDutyNow}
          value={fmt.count(data.onDutyNow)}
          detail={`${copy.ofActive} ${fmt.count(data.activeGuards)}`}
          href={can(user.role, "tracking.read") ? "/dashboard/tracking" : undefined}
        />
        <Tile
          label={copy.coverageToday}
          value={
            data.shiftsToday === 0
              ? copy.noneTile
              : /* Not "٢ / ٢": a slash between two numerals is bidi-neutral, so
                   the run reorders in RTL and the ratio renders reversed. */
                `${fmt.count(data.shiftsCovered)} ${common.ofRequired} ${fmt.count(data.shiftsToday)}`
          }
          detail={data.shiftsToday === 0 ? undefined : copy.shiftsCovered}
          href={can(user.role, "shifts.read") ? "/dashboard/shifts" : undefined}
          attention={data.shiftsToday > 0 && data.shiftsCovered < data.shiftsToday}
          muted={data.shiftsToday === 0}
        />
        <Tile
          label={copy.lateToday}
          value={fmt.count(data.lateToday)}
          detail={`${copy.onTimeToday}: ${fmt.count(data.onTimeToday)}`}
          href={
            can(user.role, "attendance.read")
              ? `/dashboard/attendance?type=check_in&from=${today}&to=${today}`
              : undefined
          }
          attention={data.lateToday > 0}
        />
        <Tile
          label={copy.pendingLeave}
          value={fmt.count(data.pendingLeave)}
          href={can(user.role, "leave.read") ? "/dashboard/leave?status=pending" : undefined}
          attention={data.pendingLeave > 0}
        />
      </dl>

      <div className="mt-8 grid items-start gap-8 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          {/* `.ticks` — the design system's instrument marks — because this panel
              is the one an operator acts on before a shift starts. */}
          <section className="ticks [--tick-inset:12px] rounded-lg border border-n3 bg-n0 p-5 shadow-1">
            <h2 className="text-sm font-bold text-n9">{copy.gapsTitle}</h2>
            <p className="mt-1 max-w-[62ch] text-xs leading-snug text-n6">{copy.gapsLead}</p>

            {gaps.length === 0 ? (
              <p className="mt-5 text-sm text-n6">{copy.gapsEmpty}</p>
            ) : (
              <ul className="mt-4 flex flex-col gap-px overflow-hidden rounded-lg bg-n4">
                {gaps.map((gap) => (
                  <li
                    key={gap.shiftId}
                    className="flex flex-wrap items-center gap-3 bg-n0 px-4 py-3"
                  >
                    <span
                      aria-hidden
                      className="size-2.5 shrink-0 rounded-sm"
                      style={{ backgroundColor: gap.zoneColor }}
                    />
                    <Link
                      to={`/dashboard/zones/${gap.zoneId}`}
                      className="text-sm font-semibold text-n9 transition-colors duration-200 ease-brand hover:text-orange-700"
                    >
                      {gap.zoneName}
                    </Link>
                    <span className="tabular text-xs text-n7">
                      {fmt.weekday(gap.startAt)} · {fmt.time(gap.startAt)} – {fmt.time(gap.endAt)}
                    </span>
                    <StatusPill tone="attention">
                      {copy.gapsShort}{" "}
                      <span className="tabular">
                        {fmt.count(gap.requiredGuards - gap.assigned)}
                      </span>{" "}
                      {copy.gapsGuards}
                    </StatusPill>
                    {can(user.role, "shifts.write") ? (
                      <Link
                        to={`/dashboard/shifts?zone=${gap.zoneId}`}
                        className="ms-auto inline-flex items-center gap-1 text-[11px] font-semibold text-orange-700 transition-opacity duration-200 ease-brand hover:opacity-80"
                      >
                        {copy.gapsFix}
                        <ArrowLeft aria-hidden className="size-3" strokeWidth={2} />
                      </Link>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="mt-8">
            <SectionHead
              title={copy.trendTitle}
              href={can(user.role, "reports.read") ? "/dashboard/reports" : undefined}
              action={copy.trendReport}
            />
            <div className="mt-3 rounded-lg border border-n3 bg-n0 p-5 shadow-1">
              {sparkline.every((point) => point.rate === null) ? (
                <p className="py-6 text-center text-xs text-n6">{copy.trendEmpty}</p>
              ) : (
                <>
                  <TrendFigures points={sparkline} />
                  <Sparkline points={sparkline} numerals={user.numeralSystem} />
                  <div className="mt-1 flex justify-between text-[11px] text-n6" dir="ltr">
                    <span className="tabular">
                      {fmt.dateNumeric(chartInstant(sparkline[sparkline.length - 1]?.dateKey))}
                    </span>
                    <span className="tabular">{fmt.dateNumeric(chartInstant(sparkline[0]?.dateKey))}</span>
                  </div>
                </>
              )}
            </div>
          </section>

          <section className="mt-8">
            <SectionHead
              title={copy.activityTitle}
              href={can(user.role, "attendance.read") ? "/dashboard/attendance" : undefined}
              action={copy.activityAll}
            />
            {activity.length === 0 ? (
              <p className="mt-3 text-sm text-n6">{copy.activityEmpty}</p>
            ) : (
              <ul className="mt-3 flex flex-col gap-px overflow-hidden rounded-lg bg-n4">
                {activity.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex flex-wrap items-center gap-2 bg-n0 px-4 py-2.5 text-sm"
                  >
                    <PersonAvatar name={entry.guardName} photoUrl={entry.photoUrl} size="xs" />
                    <Link
                      to={`/dashboard/employees/${entry.guardId}`}
                      className="font-semibold text-n9 transition-colors duration-200 ease-brand hover:text-orange-700"
                    >
                      {entry.guardName}
                    </Link>
                    <span className="text-xs text-n7">
                      {entry.type === "check_in" ? copy.activityCheckIn : copy.activityCheckOut}
                    </span>
                    {entry.zoneName ? (
                      <span className="text-xs text-n6">{entry.zoneName}</span>
                    ) : null}
                    {entry.status === "outside_zone" ? (
                      <StatusPill tone="attention">{copy.activityOutside}</StatusPill>
                    ) : null}
                    <span className="tabular ms-auto text-[11px] text-n6">
                      {fmt.time(entry.occurredAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <aside className="min-w-0">
          <section className="rounded-lg border border-n3 bg-n0 p-5 shadow-1">
            <h2 className="flex items-center gap-2 text-sm font-bold text-n9">
              {alerts.length > 0 ? (
                <AlertTriangle aria-hidden className="size-4 text-orange-700" strokeWidth={1.8} />
              ) : null}
              {copy.alertsTitle}
            </h2>

            {alerts.length === 0 ? (
              <p className="mt-3 text-sm text-n6">{copy.alertsEmpty}</p>
            ) : (
              <ul className="mt-4 flex flex-col gap-2">
                {alerts.map((alert) => (
                  <li key={alert.label}>
                    <Link
                      to={alert.href}
                      className="flex items-center gap-3 rounded-md border border-n3 bg-n1 px-3 py-2.5 transition-colors duration-200 ease-brand hover:border-orange/45 hover:bg-orange/8"
                    >
                      <span className="tabular text-base font-bold text-orange-700">
                        {fmt.count(alert.count)}
                      </span>
                      <span className="min-w-0 flex-1 text-xs leading-snug text-n8">
                        {alert.label}
                      </span>
                      <ArrowLeft
                        aria-hidden
                        className="size-3.5 shrink-0 text-n5"
                        strokeWidth={2}
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* The signal states, which only tracking can resolve. Split out from
              the alert list because "no signal" is a condition of the fleet
              rather than a task with an owner. */}
          {can(user.role, "tracking.read") ? (
            <dl className="mt-6 grid gap-px overflow-hidden rounded-lg bg-n4 sm:grid-cols-2">
              <Tile
                label={trackingCopy.stale}
                value={fmt.count(data.staleSignals)}
                href="/dashboard/tracking"
                attention={data.staleSignals > 0}
                compact
              />
              <Tile
                label={copy.neverPinged}
                value={fmt.count(data.neverPinged)}
                href="/dashboard/tracking"
                attention={data.neverPinged > 0}
                compact
              />
              <Tile
                label={attendanceCopy.unclosedTitle}
                value={fmt.count(data.unclosedPunches)}
                href={can(user.role, "attendance.read") ? "/dashboard/attendance" : undefined}
                attention={data.unclosedPunches > 0}
                compact
              />
              <Tile
                label={copy.understaffedZones}
                value={fmt.count(data.understaffedZones)}
                href="/dashboard/zones"
                attention={data.understaffedZones > 0}
                compact
              />
            </dl>
          ) : null}
        </aside>
      </div>
    </div>
  );
}

/** One KPI tile. */
function Tile({
  label,
  value,
  detail,
  href,
  attention,
  muted,
  compact,
}: {
  label: string;
  value: string;
  detail?: string;
  href?: string;
  attention?: boolean;
  muted?: boolean;
  compact?: boolean;
}) {
  const body = (
    <>
      <dt className="relative text-[11px] font-semibold tracking-[0.04em] text-n6">{label}</dt>
      <dd
        className={cn(
          "tabular relative mt-2 font-bold",
          compact ? "text-base" : "text-2xl leading-none",
          muted ? "text-n6" : attention ? "text-orange-700" : "text-n9",
        )}
      >
        {value}
      </dd>
      {detail ? (
        <p className="relative mt-1.5 text-[11px] text-n6">{detail}</p>
      ) : null}
      {href ? (
        <ArrowLeft aria-hidden className="absolute end-3 bottom-3 size-3.5 text-n5" strokeWidth={2} />
      ) : null}
    </>
  );

  /* A tile only becomes a link when there is a list to land on. An inert tile
     that looks clickable is worse than one that plainly is not. */
  return href ? (
    <Link
      to={href}
      className={cn(
        "relative overflow-hidden bg-n0 transition-colors duration-200 ease-brand hover:bg-n2",
        compact ? "p-4 pb-8" : "p-5 pb-8",
      )}
    >
      {body}
    </Link>
  ) : (
    <div className={cn("relative overflow-hidden bg-n0", compact ? "p-4" : "p-5")}>{body}</div>
  );
}
